import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, PenTool, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { AuthContext } from '../App';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WritingTest = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [currentTask, setCurrentTask] = useState(0);
  const [answers, setAnswers] = useState({});
  const [wordCounts, setWordCounts] = useState({});
  const [timeLeft, setTimeLeft] = useState(2400); // 40 minutes
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    startSession();
  }, []);
  
  useEffect(() => {
    if (session && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      handleSubmit();
    }
  }, [timeLeft, session]);
  
  const startSession = async () => {
    try {
      const response = await axios.post(`${API}/writing/start-session`, {}, {
        withCredentials: true
      });
      setSession(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Önce seviye tespit sınavı yapmalısınız');
        navigate('/dashboard');
      } else {
        toast.error('Oturum başlatılamadı');
      }
    }
  };
  
  const handleTextChange = (taskId, text) => {
    setAnswers({
      ...answers,
      [taskId]: text
    });
    
    // Count words
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCounts({
      ...wordCounts,
      [taskId]: words.length
    });
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/writing/submit`,
        {
          session_id: session.session_id,
          answers: Object.entries(answers).map(([taskId, text]) => ({
            task_id: parseInt(taskId),
            text: text
          }))
        },
        { withCredentials: true }
      );
      
      toast.success('Writing testi tamamlandı! AI değerlendiriyor...');
      navigate(`/writing-results/${session.session_id}`);
    } catch (error) {
      toast.error('Test gönderilemedi');
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Test hazırlanıyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  if (!session) return null;
  
  const task = session.tasks[currentTask];
  const progress = ((currentTask + 1) / session.tasks.length) * 100;
  const wordCount = wordCounts[currentTask] || 0;
  const minWords = task.min_words || 150;
  const isWordCountValid = wordCount >= minWords;
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Button>
          
          <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 rounded-full">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-600">
              {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
            </span>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Görev {currentTask + 1} / {session.tasks.length}</span>
            <span className="text-sm font-semibold text-blue-600">Seviye: {session.level}</span>
          </div>
          <Progress value={progress} />
        </div>
        
        <Card className="glass-effect border-none shadow-xl mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PenTool className="w-6 h-6 text-purple-600" />
                Writing Task {currentTask + 1}
              </div>
              <div className={`text-sm px-3 py-1 rounded-full ${
                isWordCountValid ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {wordCount} / {minWords} kelime
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">Görev:</p>
              <p className="text-gray-800">{task.prompt}</p>
              <p className="text-xs text-gray-600 mt-2">
                Minimum {minWords} kelime • {task.time_limit} dakika
              </p>
            </div>
            
            {/* Writing Area */}
            <div>
              <textarea
                value={answers[currentTask] || ''}
                onChange={(e) => handleTextChange(currentTask, e.target.value)}
                placeholder="Yazınızı buraya yazın..."
                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none min-h-[400px] font-mono text-gray-800"
              />
              
              {!isWordCountValid && wordCount > 0 && (
                <p className="text-sm text-orange-600 mt-2">
                  Daha {minWords - wordCount} kelime yazmanız gerekiyor
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            onClick={() => setCurrentTask(Math.max(0, currentTask - 1))}
            disabled={currentTask === 0}
            variant="outline"
          >
            Önceki Görev
          </Button>
          
          <div className="flex gap-3">
            {currentTask < session.tasks.length - 1 ? (
              <Button 
                onClick={() => setCurrentTask(currentTask + 1)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Sonraki Görev
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                className="bg-green-600 hover:bg-green-700"
                disabled={submitting || !isWordCountValid}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Testi Bitir
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default WritingTest;
